/**
 * ============================================================================
 * TRANSACTION TIMELINE COMPONENT
 * ============================================================================
 * Dashboard-wt compatible; safe when formatTime is missing.
 */

import React from 'react';
import { useI18n } from '../../providers';

/**
 * Transaction Timeline Component
 * @param {Object} props
 * @param {Object} props.transaction - Transaction object
 * @returns {JSX.Element}
 */
const TransactionTimeline = ({ transaction }) => {
  const i18n = useI18n();
  const formatDate = typeof i18n?.formatDate === 'function' ? i18n.formatDate : (d) => (d ? new Date(d).toLocaleDateString() : '');
  const formatTime = typeof i18n?.formatTime === 'function' ? i18n.formatTime : (d) => (d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
  const language = i18n?.language || 'en';

  if (!transaction) return null;

  const timeline = [
    {
      status: 'created',
      label: language === 'fr' ? 'Transaction créée' : 'Transaction created',
      date: transaction.createdAt,
      icon: '📝',
      color: 'from-blue-500 to-cyan-500',
      completed: true,
    },
    {
      status: 'escrow_funded',
      label: language === 'fr' ? 'Paiement reçu' : 'Payment received',
      date: transaction.paymentReceivedAt || transaction.escrowFundedAt || transaction.paidAt,
      icon: '💰',
      color: 'from-green-500 to-emerald-500',
      completed: ['escrow_funded', 'in_progress', 'delivered', 'completed'].includes(transaction.status),
    },
    {
      status: 'in_progress',
      label: language === 'fr' ? 'En cours' : 'In progress',
      date: transaction.inProgressAt,
      icon: '⚙️',
      color: 'from-purple-500 to-pink-500',
      completed: ['in_progress', 'delivered', 'completed'].includes(transaction.status),
    },
    {
      status: 'delivered',
      label: language === 'fr' ? 'Livré' : 'Delivered',
      date: transaction.deliveredAt,
      icon: '📦',
      color: 'from-indigo-500 to-blue-500',
      completed: ['delivered', 'completed'].includes(transaction.status),
    },
    {
      status: 'completed',
      label: language === 'fr' ? 'Complété' : 'Completed',
      date: transaction.completedAt || transaction.approvedAt,
      icon: '✅',
      color: 'from-green-500 to-teal-500',
      completed: transaction.status === 'completed',
    },
  ].filter((item) => item.date || item.completed);

  return (
    <div className="tx-timeline relative space-y-4">
      {timeline.map((item, index) => (
        <div
          key={item.status}
          className="tx-timeline-item relative flex gap-4"
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          {index < timeline.length - 1 && (
            <div
              className={`tx-timeline-line absolute left-6 top-12 w-0.5 h-full ${
                item.completed ? 'tx-timeline-line-done' : 'tx-timeline-line-pending'
              }`}
            />
          )}
          <div
            className={`tx-timeline-icon relative z-10 flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 ${
              item.completed ? 'tx-timeline-icon-done' : 'tx-timeline-icon-pending'
            }`}
          >
            <span className="text-xl">{item.icon}</span>
          </div>
          <div className={`tx-timeline-content flex-1 min-w-0 ${item.completed ? 'tx-timeline-content-done' : 'tx-timeline-content-pending'}`}>
            <div className="font-semibold mb-1">{item.label}</div>
            {item.date && (
              <div className="tx-timeline-meta flex items-center gap-2 text-sm">
                <span>{formatDate(item.date)}</span>
                <span>•</span>
                <span>{formatTime(item.date)}</span>
              </div>
            )}
            {!item.completed && (
              <div className="text-xs opacity-80 mt-1">{language === 'fr' ? 'En attente...' : 'Pending...'}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TransactionTimeline;
